import type { FC } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { PapiDeviation } from '@wix/da-papi-types';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import { formatDollars } from '@wix/da-shared-react/pkg/utils/formatting';
import TierStateBadges from '@wix/da-shared-react/pkg/Tier/TierStateBadges';
import DeviationThumb from '../../_partials/DeviationThumb';
import BaseListItem from '../../items/BaseListItem';
import BaseListCell from '../../items/BaseListItem/BaseListCell';
import DeviationTitle from '../../_partials/DeviationTitle';
import DeviationActions from '../../../tableActions/DeviationActions';
import StatItem from '../../_partials/StatItem';
import DeviationInfo from '../../_partials/DeviationInfo';
import { useEditDeviationClick } from '../../../../hooks/useEditDeviationClick';
import { StudioDeviationListContext } from '../../context';

import s from './TierListItem.scss';

interface Props {
  deviation: PapiDeviation;
  offset: number;
  isUnlisted?: boolean;
}

const TierListItem: FC<Props> = ({ deviation, offset, isUnlisted }) => {
  const { t } = useTranslation();
  const { onEditClick } = useEditDeviationClick(deviation);

  const { actionsColRef, actionsColWidth } = useContext(StudioDeviationListContext);
  // We use the first row as a probe to determine the size of columns
  const shouldAddColumnRefs = offset === 0;

  const tier = deviation.tier;
  if (!tier) {
    return null;
  }

  const deviationsCount = tier.stats?.deviations ?? 0;
  const subscribersCount = tier.stats?.subscribers ?? 0;
  const tierPrice = tier.dollarPrice;
  const dollarPrice = tierPrice && formatDollars(parseFloat(tierPrice));
  const isPendingDeletion = tier.state === 'pending_deletion';
  let mainCta;

  if (isPendingDeletion) {
    mainCta = (
      <TierStateBadges
        className={s['pending-deletion']}
        badgeClassName={s['badge']}
        deviation={deviation}
        showPendingDeletionState
        withTooltip={false}
      />
    );
  } else if (isUnlisted) {
    mainCta = (
      <GhostButton variant="main" size="medium" rounded onClick={onEditClick}>
        {t('studio.products.actions.publish')}
      </GhostButton>
    );
  } else {
    mainCta = (
      <GhostButton
        variant="main"
        size="medium"
        rounded
        href={Url.studioTier(tier.tierId, 'deviations')}
      >
        {t('common.manage')}
      </GhostButton>
    );
  }
  return (
    <BaseListItem>
      <BaseListCell>
        <DeviationThumb deviation={deviation} />
      </BaseListCell>
      <BaseListCell className={s['cell-title']}>
        <DeviationTitle deviation={deviation} showSaleIndicators={false} />
        <DeviationInfo deviation={deviation} />
      </BaseListCell>
      <BaseListCell>
        <StatItem title={t('studio.products.tier.stats.pricePerMonth')} value={dollarPrice} />
      </BaseListCell>
      <BaseListCell>
        <StatItem
          title={t('studio.products.tier.stats.deviationCount')}
          value={deviationsCount}
          linkTo={deviationsCount > 0 ? Url.studioTier(tier.tierId, 'deviations') : undefined}
          disabled={isUnlisted}
        />
      </BaseListCell>
      <BaseListCell>
        <StatItem
          title={t('studio.products.tier.stats.subscribersCount')}
          value={subscribersCount}
          linkTo={subscribersCount > 0 ? Url.customerDashboard(tier.subproductId) : undefined}
          disabled={isUnlisted}
        />
      </BaseListCell>
      <BaseListCell ref={shouldAddColumnRefs ? actionsColRef : undefined} overflowHidden>
        <DeviationActions
          deviation={deviation}
          offset={offset}
          mainCta={mainCta}
          columnWidth={actionsColWidth}
        />
      </BaseListCell>
    </BaseListItem>
  );
};
TierListItem.displayName = 'TierListItem';

export default TierListItem;
